import React, { useEffect, useState, memo } from 'react';
import { HeartIcon, StarIcon } from 'lucide-react';
interface Review {
  id: string;
  name: string;
  text: string;
  rating: number;
  likes: number;
  timeAgo: string;
}
const SEED_REVIEWS: Review[] = [{
  id: 's1',
  name: '@sarah_momof2',
  text: 'My son finally finishes his activities without melting down. We do 10 minutes every morning and his focus has completely changed. Worth every penny. ❤️',
  rating: 5,
  likes: 47,
  timeAgo: '2 min ago'
}, {
  id: 's2',
  name: '@the_miller_family',
  text: 'The best part is zero screens and zero prep. I just print a page and he is instantly engaged. His memory games are his favorite.',
  rating: 5,
  likes: 38,
  timeAgo: '8 min ago'
}, {
  id: 's3',
  name: '@dad_of_twins',
  text: 'I was skeptical about a printable, but the difference in my daughter\'s confidence is real. She actually asks to do "brain time" now.',
  rating: 5,
  likes: 92,
  timeAgo: '15 min ago'
}, {
  id: 's4',
  name: '@jess_and_liam',
  text: 'My 5-year-old used to give up the second something got hard. After a few weeks he pushes through puzzles on his own. Huge win for us.',
  rating: 5,
  likes: 56,
  timeAgo: '23 min ago'
}, {
  id: 's5',
  name: '@momma.bear.emily',
  text: 'The Calm & Self-Control volume has been a game changer for tantrums. The breathing activities actually work. I cried a little, honestly.',
  rating: 5,
  likes: 134,
  timeAgo: '31 min ago'
}, {
  id: 's6',
  name: '@rachel_specialneeds',
  text: 'My son has ADHD and nothing held his attention like this. He feels capable for the first time. That alone is priceless.',
  rating: 5,
  likes: 78,
  timeAgo: '42 min ago'
}, {
  id: 's7',
  name: '@homeschool_hannah',
  text: 'I homeschool three kids and having structured brain activities ready to print saves my sanity. Each one picks their level. Brilliant.',
  rating: 5,
  likes: 41,
  timeAgo: '1 h ago'
}, {
  id: 's8',
  name: '@david.k',
  text: 'I come home tired from work, print one page, and my son is excited to do it with me. No planning needed. We both look forward to it.',
  rating: 5,
  likes: 29,
  timeAgo: '1 h ago'
}, {
  id: 's9',
  name: '@grandma_olivia',
  text: 'I bought this for my grandkids. Family Brain Night has become our favorite tradition. So much laughing and learning together. 🥺',
  rating: 5,
  likes: 167,
  timeAgo: '2 h ago'
}, {
  id: 's10',
  name: '@megan.mom.of.dos',
  text: 'I recommend this to every parent I know. The detective and escape room bonuses keep my daughter busy for hours — screen-free.',
  rating: 5,
  likes: 53,
  timeAgo: '3 h ago'
}, {
  id: 's11',
  name: '@the_brooks_house',
  text: "Six months in and the difference in my son's reading and reasoning is obvious. His teacher even noticed. Worth ten times the price.",
  rating: 5,
  likes: 84,
  timeAgo: '4 h ago'
}, {
  id: 's12',
  name: '@ryan_dad',
  text: 'I get my kids on weekends and this gave us a real routine. They ask for their brain pages every Saturday morning now. Lifesaver.',
  rating: 5,
  likes: 198,
  timeAgo: '5 h ago'
}];
const STORAGE_KEY = 'littlegenius_reviews_v1';
const LIKES_KEY = 'littlegenius_likes_v1';
export function CommunityReviewsSection() {
  const [reviews, setReviews] = useState<Review[]>(SEED_REVIEWS);
  const [likedIds, setLikedIds] = useState<Set<string>>(new Set());
  const [visibleCount, setVisibleCount] = useState(5);
  // Load saved state
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved) as Review[];
        if (Array.isArray(parsed) && parsed.length > 0) {
          setReviews([...parsed, ...SEED_REVIEWS].slice(0, 15));
        }
      }
      const liked = localStorage.getItem(LIKES_KEY);
      if (liked) {
        setLikedIds(new Set(JSON.parse(liked) as string[]));
      }
    } catch {
      // ignore
    }
  }, []);
  const persistLikes = (next: Set<string>) => {
    try {
      localStorage.setItem(LIKES_KEY, JSON.stringify(Array.from(next)));
    } catch {
      // ignore
    }
  };
  const handleLike = (id: string) => {
    setReviews(prev => prev.map(r => {
      if (r.id !== id) return r;
      const isLiked = likedIds.has(id);
      return {
        ...r,
        likes: r.likes + (isLiked ? -1 : 1)
      };
    }));
    setLikedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);else next.add(id);
      persistLikes(next);
      return next;
    });
  };
  const visibleReviews = reviews.slice(0, visibleCount);
  const hasMore = visibleCount < reviews.length;
  return <section className="py-12 md:py-20 px-4 bg-black text-white" data-id="element-55">
      <div className="max-w-3xl mx-auto" data-id="element-56">
        <div className="text-center mb-8 md:mb-10" data-id="element-57">
          <h2 className="text-3xl md:text-5xl font-black text-white mb-3 md:mb-4" data-id="element-58">
            ❤️ What Families Are Saying
          </h2>
          <p className="text-base md:text-lg text-gray-400 font-medium" data-id="element-59">
            Over 12,000 families worldwide are building stronger brains with The
            Little Genius System™
          </p>
          <div className="flex items-center justify-center gap-2 mt-3" data-id="element-60">
            <div className="flex" data-id="element-61">
              {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-5 h-5 md:w-6 md:h-6 fill-yellow-400 text-yellow-400" data-id="element-62" />)}
            </div>
            <span className="text-sm md:text-base text-gray-300 font-bold" data-id="element-63">
              4.9 / 5
            </span>
            <span className="text-xs md:text-sm text-gray-500" data-id="element-64">
              · {reviews.length} reviews
            </span>
          </div>
        </div>

        {/* Reviews list */}
        <div className="space-y-4" data-id="element-65">
          {visibleReviews.map(review => {
          const initial = review.name.replace('@', '').charAt(0).toUpperCase();
          const isLiked = likedIds.has(review.id);
          return <div key={review.id} className="bg-gray-900 border border-gray-800 rounded-2xl p-5 md:p-6" data-id="element-66">
                <div className="flex items-start justify-between mb-3 gap-3" data-id="element-67">
                  <div className="flex items-center gap-3 min-w-0 flex-1" data-id="element-68">
                    <div className="w-10 h-10 md:w-11 md:h-11 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-69">
                      <span className="text-black font-black text-sm md:text-base" data-id="element-70">
                        {initial}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1" data-id="element-71">
                      <p className="text-sm md:text-base font-bold text-white truncate" data-id="element-72">
                        {review.name}
                      </p>
                      <p className="text-[10px] md:text-xs text-gray-500" data-id="element-73">
                        {review.timeAgo}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-shrink-0" data-id="element-74">
                    {[...Array(5)].map((_, idx) => <StarIcon key={idx} className={`w-4 h-4 md:w-5 md:h-5 ${idx < review.rating ? 'fill-yellow-400 text-yellow-400' : 'fill-transparent text-gray-600'}`} data-id="element-75" />)}
                  </div>
                </div>
                <p className="text-sm md:text-base text-gray-300 font-medium leading-relaxed mb-4" data-id="element-76">
                  {review.text}
                </p>
                <div className="flex items-center" data-id="element-77">
                  <button onClick={() => handleLike(review.id)} className={`inline-flex items-center gap-2 text-sm font-bold transition-colors cursor-pointer ${isLiked ? 'text-pink-400' : 'text-gray-500 hover:text-pink-400'}`} aria-label={isLiked ? 'Unlike' : 'Like'} data-id="element-78">
                    <HeartIcon className={`w-5 h-5 transition-all ${isLiked ? 'fill-pink-400 text-pink-400 scale-110' : 'fill-transparent'}`} data-id="element-79" />
                    <span data-id="element-80">{review.likes}</span>
                  </button>
                </div>
              </div>;
        })}
        </div>

        {hasMore && <div className="text-center mt-6 md:mt-8" data-id="element-81">
            <button onClick={() => setVisibleCount(c => Math.min(c + 5, reviews.length))} className="inline-flex items-center gap-2 px-5 md:px-6 py-2.5 md:py-3 rounded-xl border border-gray-700 bg-gray-900 hover:bg-gray-800 text-white font-bold text-sm md:text-base transition-colors cursor-pointer" data-id="element-82">
              Show more reviews ({reviews.length - visibleCount})
            </button>
          </div>}
      </div>
    </section>;
}